/**
 * La ficha de una variante («Verde / M»), al pulsarla en el inventario.
 *
 * La tabla se queda en lo que se lee de un vistazo —nombre, precio, cuántas
 * quedan— y lo demás vive aquí, agrupado como en Shopify:
 *   · su foto: de las del producto o una nueva; con color, para todas las de
 *     ese color de una vez (lo normal: la camiseta verde es verde en S y en M);
 *   · precio, «antes» (tachado en la tienda) y costo, con la ganancia y el
 *     margen calculados —el costo nunca sale en la tienda—;
 *   · existencias, SKU y código de barras;
 *   · si se vende.
 *
 * `all`: la misma ficha para todas a la vez (precio y existencias), con el
 * generador de SKU.
 *
 * Edita una copia: «Listo» la aplica al borrador del asistente y «Cancelar» la
 * suelta. El borrador se guarda con el resto al terminar el asistente.
 */
import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { IconBarcode, IconCheck, IconPhotoPlus, IconX } from '@tabler/icons-react'
import { useI18n } from '../../../lib/i18n'
import { ImageLibraryPickerDialog } from '../../ImageLibraryPickerDialog'
import { formatMoney, toMinorUnits } from './offerShared'
import type { VariantRow } from './VariantsEditor'

export type VariantPhotoScope = 'variant' | 'value'

export function VariantDialog({
  mode, label, tone, row, currency, basePrice, baseAmount, photos, projectId, token,
  valueLabel, valuePhoto, onApply, onGenerateSkus, onClose,
}: {
  mode: 'one' | 'all'
  /** «Verde / M», o «Todas las variantes». */
  label: string
  tone: string | null
  row: VariantRow
  currency: string
  /** El precio del producto ya escrito: lo que cuesta sin precio propio. */
  basePrice: string
  /** El mismo, en centavos, para la ganancia. */
  baseAmount: number | null
  photos: string[]
  projectId: string
  token: string
  /** El valor de color de esta variante («Verde»), si lo hay: su foto vale para todas las de ese color. */
  valueLabel: string | null
  valuePhoto: string
  onApply: (row: VariantRow, photo: { url: string; scope: VariantPhotoScope } | null) => void
  onGenerateSkus?: () => void
  onClose: () => void
}) {
  const { t } = useI18n()
  const [draft, setDraft] = useState<VariantRow>(row)
  const [photo, setPhoto] = useState<string>(row.image || valuePhoto)
  const [scope, setScope] = useState<VariantPhotoScope>(row.image ? 'variant' : 'value')
  const [picking, setPicking] = useState(false)
  const set = (next: Partial<VariantRow>) => setDraft((current) => ({ ...current, ...next }))

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape' && !picking) onClose() }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose, picking])

  /* La ganancia de una unidad: su precio (o el del producto) menos el costo. */
  const priceMinor = draft.price.trim() ? toMinorUnits(draft.price, currency) : baseAmount
  const costMinor = draft.cost.trim() ? toMinorUnits(draft.cost, currency) : null
  const profit = priceMinor !== null && costMinor !== null ? priceMinor - costMinor : null
  const margin = profit !== null && priceMinor ? Math.round((profit / priceMinor) * 100) : null
  const compareMinor = draft.compareAt.trim() ? toMinorUnits(draft.compareAt, currency) : null
  const compareLow = compareMinor !== null && priceMinor !== null && compareMinor <= priceMinor

  const money = (field: 'price' | 'compareAt' | 'cost') => (event: React.ChangeEvent<HTMLInputElement>) => set({ [field]: event.target.value.replace(/[^\d.,]/g, '') })
  const apply = () => {
    const photoChanged = photo !== (row.image || valuePhoto)
    const photoChoice = photoChanged || (scope === 'variant') !== Boolean(row.image)
      ? { url: photo, scope: valueLabel ? scope : 'variant' as VariantPhotoScope }
      : null
    onApply(draft, mode === 'one' ? photoChoice : null)
    onClose()
  }

  /* En el body y no donde se monta: dentro de la tarjeta del asistente, las
     reglas de `.dash-settings-card .dash-field` le ganaban a la receta de iOS y
     en celular los campos salían escalados y cortados por abajo. */
  return createPortal(
    <div className="dash-modal-backdrop dash-vdlg-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose() }}>
      <section className="dash-modal dash-coupon-modal dash-vdlg" role="dialog" aria-modal="true" aria-label={label}>
        <header className="dash-coupon-modal-head">
          {mode === 'one' && photo
            ? <img className="dash-vdlg-thumb" src={photo} alt="" />
            : tone ? <i className="dash-vx-swatch dash-vdlg-swatch" style={{ background: tone }} aria-hidden="true" /> : null}
          <div>
            <h2>{label}</h2>
            <p>{t(mode === 'all' ? 'offers.variants.dialog.allLead' : 'offers.variants.dialog.lead')}</p>
          </div>
          <button type="button" className="dash-icon-btn" aria-label={t('common.close')} onClick={onClose}><IconX size={16} /></button>
        </header>

        <div className="dash-coupon-modal-body dash-vdlg-body">
          {mode === 'one' ? (
            <section className="dash-vdlg-section">
              <h3>{t('offers.variants.dialog.photo')}</h3>
              <div className="dash-vdlg-photos">
                {photos.map((url) => (
                  <button key={url} type="button" className={photo === url ? 'is-on' : undefined} aria-pressed={photo === url} onClick={() => setPhoto(photo === url ? '' : url)}>
                    <img src={url} alt="" loading="lazy" />
                    {photo === url ? <span aria-hidden="true"><IconCheck size={12} /></span> : null}
                  </button>
                ))}
                {photo && !photos.includes(photo) ? (
                  <button type="button" className="is-on" aria-pressed="true" onClick={() => setPhoto('')}>
                    <img src={photo} alt="" />
                    <span aria-hidden="true"><IconCheck size={12} /></span>
                  </button>
                ) : null}
                <button type="button" className="is-add" onClick={() => setPicking(true)}>
                  <IconPhotoPlus size={16} aria-hidden="true" />
                  <small>{t('offers.variants.dialog.upload')}</small>
                </button>
              </div>
              {valueLabel && photo ? (
                <label className="dash-vdlg-check">
                  <input type="checkbox" checked={scope === 'value'} onChange={(event) => setScope(event.target.checked ? 'value' : 'variant')} />
                  <span>{t('offers.variants.dialog.photoForAll', { value: valueLabel })}</span>
                </label>
              ) : null}
              <small className="dash-vdlg-hint">{t('offers.variants.dialog.photoHint')}</small>
            </section>
          ) : null}

          <section className="dash-vdlg-section">
            <h3>{t('offers.variants.dialog.pricing')}</h3>
            <div className="dash-vdlg-grid">
              <label className="dash-field">
                <span>{t('offers.variants.colPrice')}</span>
                <input inputMode="decimal" value={draft.price} placeholder={basePrice} onChange={money('price')} />
              </label>
              {mode === 'one' ? (
                <>
                  <label className="dash-field">
                    <span>{t('offers.variants.dialog.compareAt')}</span>
                    <input inputMode="decimal" value={draft.compareAt} placeholder="—" onChange={money('compareAt')} />
                  </label>
                  <label className="dash-field">
                    <span>{t('offers.variants.dialog.cost')}</span>
                    <input inputMode="decimal" value={draft.cost} placeholder="—" onChange={money('cost')} />
                  </label>
                </>
              ) : null}
            </div>
            {mode === 'one' ? (
              <small className={`dash-vdlg-hint${compareLow ? ' is-warn' : ''}`}>
                {compareLow
                  ? t('offers.variants.dialog.compareLow')
                  : profit !== null && margin !== null
                    ? t('offers.variants.dialog.margin', { profit: formatMoney(profit, currency), margin })
                    : t('offers.variants.dialog.pricingHint')}
              </small>
            ) : null}
          </section>

          <section className="dash-vdlg-section">
            <h3>{t('offers.variants.inventory')}</h3>
            <div className="dash-vdlg-grid">
              <label className="dash-field">
                <span>{t('offers.variants.colStock')}</span>
                <input inputMode="numeric" value={draft.stock} placeholder="∞" onChange={(event) => set({ stock: event.target.value.replace(/[^\d]/g, '') })} />
              </label>
              {mode === 'one' ? (
                <>
                  <label className="dash-field">
                    <span>{t('offers.variants.colSku')}</span>
                    <input value={draft.sku} maxLength={64} onChange={(event) => set({ sku: event.target.value })} />
                  </label>
                  <label className="dash-field">
                    <span>{t('offers.variants.dialog.barcode')}</span>
                    <input value={draft.barcode} maxLength={64} placeholder={t('offers.variants.dialog.barcodeHint')} onChange={(event) => set({ barcode: event.target.value })} />
                  </label>
                </>
              ) : null}
            </div>
            <small className="dash-vdlg-hint">{t('offers.variants.dialog.stockHint')}</small>
            {mode === 'all' && onGenerateSkus ? (
              <button type="button" className="dash-secondary-btn dash-vdlg-sku" onClick={() => { onGenerateSkus(); }}>
                <IconBarcode size={14} aria-hidden="true" /> {t('offers.variants.generateSku')}
              </button>
            ) : null}
          </section>

          {mode === 'one' ? (
            <section className="dash-vdlg-section dash-vdlg-row">
              <span>
                <strong>{t('offers.variants.colSelling')}</strong>
                <small className="dash-vdlg-hint">{t(draft.active ? 'offers.variants.dialog.sellingOn' : 'offers.variants.dialog.sellingOff')}</small>
              </span>
              <button
                type="button"
                className={`dash-page-settings-switch${draft.active ? ' is-on' : ''}`}
                aria-pressed={draft.active}
                aria-label={t('offers.variants.colSelling')}
                onClick={() => set({ active: !draft.active })}
              ><i /></button>
            </section>
          ) : null}
        </div>

        <footer>
          <button type="button" className="dash-secondary-btn" onClick={onClose}>{t('common.cancel')}</button>
          <button type="button" className="dash-primary-btn" onClick={apply}>{t(mode === 'all' ? 'offers.variants.dialog.applyAll' : 'offers.variants.dialog.done')}</button>
        </footer>
      </section>

      {picking ? (
        <ImageLibraryPickerDialog
          token={token}
          projectId={projectId}
          onSelect={(url) => { setPhoto(url); setPicking(false) }}
          onClose={() => setPicking(false)}
        />
      ) : null}
    </div>,
    document.body,
  )
}
